import { useRef, useState, useEffect, useCallback } from 'react';

interface SignaturePadProps {
  onSign: (signatureName: string, signatureDataUrl: string) => void;
  brandColor: string;
  disabled?: boolean;
  companyName?: string;
}

export function SignaturePad({ onSign, brandColor, disabled, companyName }: SignaturePadProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasDrawn, setHasDrawn] = useState(false);
  const [signatureName, setSignatureName] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Initialize canvas
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Set canvas size to match display size (handle high-DPI)
    const rect = canvas.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;
    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;
    ctx.scale(dpr, dpr);

    // Set drawing styles
    ctx.strokeStyle = '#1f2937';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
  }, []);

  const getPoint = useCallback((e: React.MouseEvent | React.TouchEvent) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    const rect = canvas.getBoundingClientRect();

    if ('touches' in e) {
      const touch = e.touches[0] || e.changedTouches[0];
      return { x: touch.clientX - rect.left, y: touch.clientY - rect.top };
    }
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  }, []);

  const startDrawing = useCallback((e: React.MouseEvent | React.TouchEvent) => {
    if (disabled) return;
    e.preventDefault();
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!ctx) return;

    const point = getPoint(e);
    ctx.beginPath();
    ctx.moveTo(point.x, point.y);
    setIsDrawing(true);
  }, [disabled, getPoint]);

  const draw = useCallback((e: React.MouseEvent | React.TouchEvent) => {
    if (!isDrawing || disabled) return;
    e.preventDefault();
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!ctx) return;

    const point = getPoint(e);
    ctx.lineTo(point.x, point.y);
    ctx.stroke();
    setHasDrawn(true);
  }, [isDrawing, disabled, getPoint]);

  const stopDrawing = useCallback(() => {
    setIsDrawing(false);
  }, []);

  const clearCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!ctx || !canvas) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setHasDrawn(false);
  }, []);

  const handleSubmit = useCallback(async () => {
    const canvas = canvasRef.current;
    if (!canvas || !hasDrawn || !signatureName.trim()) return;
    setSubmitting(true);
    try {
      const dataUrl = canvas.toDataURL('image/png');
      await onSign(signatureName.trim(), dataUrl);
    } finally {
      setSubmitting(false);
    }
  }, [hasDrawn, signatureName, onSign]);

  const canSubmit = hasDrawn && signatureName.trim().length > 0 && !disabled && !submitting;

  return (
    <div className="pp-sig-container">
      {/* Legal text */}
      <p className="pp-sig-legal">
        By signing below, you accept the terms and conditions of this proposal
        {companyName ? ` and authorize ${companyName} to proceed with the work described above` : ''}.
      </p>

      {/* Signature canvas */}
      <div className="pp-sig-canvas-wrap">
        <label className="pp-sig-label">Signature</label>
        <canvas
          ref={canvasRef}
          className="pp-sig-canvas"
          onMouseDown={startDrawing}
          onMouseMove={draw}
          onMouseUp={stopDrawing}
          onMouseLeave={stopDrawing}
          onTouchStart={startDrawing}
          onTouchMove={draw}
          onTouchEnd={stopDrawing}
        />
        <div className="pp-sig-controls">
          <button type="button" className="pp-sig-clear" onClick={clearCanvas} disabled={!hasDrawn || disabled}>
            Clear
          </button>
        </div>
      </div>

      {/* Name input */}
      <div className="pp-sig-name-wrap">
        <label className="pp-sig-label">Printed Name</label>
        <input
          type="text"
          className="pp-sig-name"
          placeholder="Enter your full name"
          value={signatureName}
          onChange={(e) => setSignatureName(e.target.value)}
          disabled={disabled}
        />
      </div>

      {/* Submit */}
      <button
        type="button"
        className="pp-sig-submit"
        style={{ backgroundColor: canSubmit ? brandColor : undefined }}
        onClick={handleSubmit}
        disabled={!canSubmit}
      >
        {submitting ? 'Submitting...' : 'Sign & Accept Proposal'}
      </button>
    </div>
  );
}
